import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { payrollGlobalApi as papi } from '../../../api/payrollGlobal.js';

/**
 * Run list + create-a-run from an open pay calendar. The run workbench itself
 * (calculate → approve → post → payslips) lives in RunDetailPage.
 */
export default function RunsPage() {
  const [runs, setRuns] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [calendarId, setCalendarId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([papi.listRuns(), papi.listPayCalendars()]);
      setRuns(r?.items || []);
      setCalendars((c?.items || []).filter((x) => x.status === 'open'));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function createRun(e) {
    e.preventDefault();
    if (!calendarId) return;
    setBusy(true);
    setError(null);
    try {
      await papi.createRun({ payCalendarId: calendarId });
      setCalendarId('');
      await load();
    } catch (e2) {
      setError(e2.response?.data?.message || 'Failed to create run');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="payroll-global-runs-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Run Workbench</h1>
          <p className="muted">Create a payroll run for a pay calendar, then calculate → approve → post.</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="payroll-global-runs-error">{error}</div>}

      <form onSubmit={createRun} className="card form-card grid-form">
        <label>Open pay calendar
          <select data-testid="payroll-global-run-calendar-input" value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
            <option value="">— select —</option>
            {calendars.map((c) => (
              <option key={c.id} value={c.id}>{String(c.period_start).slice(0, 10)} → {String(c.period_end).slice(0, 10)}</option>
            ))}
          </select>
        </label>
        <div className="full actions">
          <button className="btn primary" type="submit" data-testid="payrollx-run-create" disabled={busy || !calendarId}>Create run</button>
        </div>
      </form>

      <div className="card">
        <h3>Runs</h3>
        <table className="data-table" data-testid="payroll-global-run-list">
          <thead><tr><th>Period</th><th>Status</th><th>Net total</th><th></th></tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} data-testid={`payroll-global-run-row-${r.id}`}>
                <td>{String(r.period_start).slice(0, 10)} → {String(r.period_end).slice(0, 10)}</td>
                <td data-testid={`payroll-global-run-status-${r.id}`}>{r.status}</td>
                <td>{r.totals?.netCents != null ? `${r.currency} ${(Number(r.totals.netCents) / 100).toLocaleString()}` : '—'}</td>
                <td><Link className="btn ghost" to={`/payroll-global/runs/${r.id}`} data-testid={`payroll-global-run-open-${r.id}`}>Open</Link></td>
              </tr>
            ))}
            {!loading && runs.length === 0 && <tr><td colSpan={4} className="muted">No runs yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
